import { isEmpty } from "lodash";
import { Sparkle } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { toast } from "sonner";

import { promptAiAssistant } from "@/apis/ai-assistant";

import { returnValidImages } from "@/lib/miscellaneous/return-valid-images";
import type { TranslationKey } from "@/localisation/types";
import posthog from "posthog-js";
import React from "react";
import { Button } from "../../components/ui/button";
import { LoadingSpinner } from "../../components/ui/spinner";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type TypewriterProps = {
  text: string;
  speed?: number;
  onFinish?: () => void;
};

const Typewriter = React.memo(
  ({ text, speed = 15, onFinish = () => {} }: TypewriterProps) => {
    const [displayedText, setDisplayedText] = useState("");

    useEffect(() => {
      let index = 0;
      const interval = setInterval(() => {
        setDisplayedText(text.slice(0, index + 1));
        index++;
        if (index === text.length - 1) {
          clearInterval(interval);
          onFinish();
        }
      }, speed);

      // Cleanup interval on unmount
      return () => clearInterval(interval);
    }, []);

    return <span>{displayedText}</span>;
  },
);

const customToast = (
  renderElement: (id: string | number) => ReactElement,
  { closeButton, duration }: { closeButton?: boolean; duration?: number } = {},
) => {
  return toast.custom(renderElement, {
    position: "top-right",
    // HARDCODE the style here, this needs to be similar to the toast component.
    style: {
      background: "var(--normal-bg)",
      border: "1px solid var(--normal-border)",
      color: "var(--normal-text)",
      borderRadius: "var(--border-radius)",
      boxShadow: "0 4px 12px #0000001a",
      width: "500px",
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontWeight: 500,
      lineHeight: 1.5,
      left: "-125px",
    },
    important: true,
    closeButton,
    duration: duration ?? Number.POSITIVE_INFINITY,
  });
};

type Props = {
  prompt: {
    human: string;
    system?: string;
  };
  query?: string;
  children: ({
    askAssistant,
  }: {
    askAssistant: () => Promise<void>;
  }) => ReactElement;
  type?: "internal" | "wordware";
  label?: string;
};

export const AskAi = ({
  prompt: { human, system = "" },
  query,
  children,
  type = "internal",
  label = "",
}: Props) => {
  const { t } = useTranslation();

  const handleAskAI = async () => {
    toast.dismiss();

    posthog.capture("ask_ai_clicked", {
      url: window.location.href,
      label,
    });

    const loadingId = customToast(() => (
      <div className="flex flex-row gap-2 items-center">
        <div className="self-start">
          <LoadingSpinner />
        </div>
        <Typewriter text={t("ask_ai.loading")} />
      </div>
    ));

    try {
      const { summary, urls, images } = await promptAiAssistant(
        human,
        system,
        query,
        type,
      );

      const validImages = await returnValidImages(images);

      setTimeout(() => {
        toast.dismiss();
      }, 10);

      // wait for the loading spinner to disappear
      await sleep(500);

      customToast(
        (id) => {
          return (
            <div className="flex flex-row gap-2 items-center h-full w-full hover:overflow-hidden">
              <div className="self-start">
                <Sparkle />
              </div>
              <div className="flex flex-col gap-4 w-full items-center">
                <Markdown>{summary}</Markdown>
                {urls.length > 0 && (
                  <ul className="list-disc">
                    {urls.map(
                      ({
                        url: { title, url },
                      }: {
                        url: { title: string; url: string };
                      }) => (
                        <li key={url}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            {title}
                          </a>
                        </li>
                      ),
                    )}
                  </ul>
                )}
                {!isEmpty(validImages) && (
                  <div className="w-full h-[240px]">
                    <div className="grid grid-cols-4 gap-4 p-4">
                      {validImages.slice(0, 8).map((src, index) => (
                        <div
                          key={index.toString()}
                          className="overflow-hidden rounded-lg"
                        >
                          <img
                            src={src}
                            alt={`image-${index}`}
                            className="object-contain h-[100px] max-w-full"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-row items-center gap-4 justify-end self-end">
                  {query && (
                    <a
                      href={`https://www.google.com/search?q=${query}`}
                      target="_blank"
                      rel="noreferrer"
                      className="pointer-events-auto underline"
                    >
                      {t("ask_ai.google_search")}
                    </a>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toast.dismiss(id)}
                    className="self-end"
                  >
                    {t("ask_ai.close")}
                  </Button>
                </div>
              </div>
            </div>
          );
        },
        {
          closeButton: true,
        },
      );
    } catch (error: any) {
      console.log(error);
      toast.dismiss(loadingId);
      // wait for the loading spinner to disappear
      await sleep(500);
      toast.error(
        t(
          `server_errors.${error.response?.data?.code}` as TranslationKey,
        ) as string,
        {
          richColors: true,
          position: "top-right",
        },
      );
    }
  };

  return <>{children({ askAssistant: handleAskAI })}</>;
};
